import { Injectable, Optional } from '@angular/core';
import { IPSObservabilityService } from '@ips/common-angular';
import { DocumentItemList1, DocumentService } from 'com.trumbull.phx.document.api-client';
import { RecordRetrievalRequestItem } from 'com.trumbull.phx.recordretrievalobjective.api-client';
import * as _ from 'lodash';
import { forkJoin, map, Observable, of } from 'rxjs';
import { DatePipe } from '@angular/common';
import { Attachment } from '../../order-info/order-info.model';

@Injectable({
  providedIn: 'root',
})
export class AttachmentProcessorService {
  private documents: any[] = [];
  private requestDocuments: any[] = [];
  private requests: any[] = [];

  constructor(private documentService: DocumentService, private datePipe: DatePipe, @Optional() private readonly observability: IPSObservabilityService) {
  }

  getMatchedDocumentsForRequests(
    requests: any[],
    vocabDocumentTagsData: any
  ): Observable<{ requestNumber: string; documentList: DocumentItemList1[] }[]> {

    const allDocuments = this.getAllDocuments();

    if (allDocuments && allDocuments.length) {
      // If requestDocuments exist, use them directly for all requests
      const results = requests.map(request => {
        const filteredDocs = this.matchRequestsToDocuments(
          allDocuments,
          request,
          vocabDocumentTagsData
        );

        return {
          requestNumber: request.requestNumber,
          documentList: filteredDocs
        };
      });

      // Return as an Observable using 'of' since no async call needed
      return of(results);
    }

    // Else, do the normal fetch per request
    const results$: Observable<{ requestNumber: string; documentList: DocumentItemList1[] }>[] =
      requests.map(request => {
        const docListIds = request.documentLists.map((dl: any) => dl.documentListId);

        const docObservables: Observable<DocumentItemList1>[] = docListIds.map((documentListId: any) =>
          this.documentService.getDocumentList('standard', documentListId) // should return DocumentItemList1
        );

        return forkJoin(docObservables).pipe(
          map((documentReferences: DocumentItemList1[]) => {
            const filteredDocs = this.matchRequestsToDocuments(
              documentReferences,
              request,
              vocabDocumentTagsData
            );
            return {
              requestNumber: request.requestNumber,
              documentList: filteredDocs
            };
          })
        );
      });

    return forkJoin(results$);
  }


  matchRequestsToDocuments(
    documentReferences: (DocumentItemList1 | undefined)[],
    request: RecordRetrievalRequestItem,
    vocabDocumentTagsData: any
  ): any[] {
    const requestNumberStr = `${request.requestNumber} - Request`.toLowerCase();

    const matchedDocuments: any[] = [];

    for (const doc of documentReferences) {
      if (!doc?.name?.toLowerCase().startsWith(requestNumberStr)) { // As suggested by Udit to change .includes to .startsWith
        continue;
      }

      const docItems = doc.documentItems || [];

      for (const docItem of docItems) {
        matchedDocuments.push({
          documentId: docItem.id ?? '',
          fileName: docItem.fileName,
          tags: this.getDocumentTagsDescription(docItem.tags, vocabDocumentTagsData),
          documentListId: docItem.documentListId,
          ...docItem, // keep this if still useful
        });
      }
    }

    return matchedDocuments;
  }

  getDocumentTagsDescription(tags: any, vocabDocumentTagsData: any) {
    let documentTags;
    if (vocabDocumentTagsData) {
      documentTags = vocabDocumentTagsData.vocabularyItems.filter((item: any) =>
        tags.some((tag: any) => tag.tagName === item.code)
      );
    }
    return (documentTags?.map((item: any) => item.description))?.join(', ')
  }

  getDocTitleDescription(title: string = '', vocabDocumentTagsData: any) {
    // const vocabTitleData = vocabData?.find((d: any) => d.vocabularyName === 'OrderDocumentCode');
    const selectedTitle = vocabDocumentTagsData?.vocabularyItems?.find((item: any) => item?.code === title);
    return selectedTitle?.description;
  }

  processAttachmentsFromDocuments(
    matchedDocs: { requestNumber: string; documentList: any }[]
  ): { requestNumber: string; documentList: Attachment[] }[] {
    const result: { requestNumber: string; documentList: Attachment[] }[] = [];

    matchedDocs.forEach(match => {
      const { requestNumber, documentList } = match;
      const attachments: Attachment[] = [];

      documentList.forEach((matchingItem: any) => {
        if (matchingItem) {
          const attachmentDetail: Attachment = {
            filename: matchingItem.fileName,
            description: matchingItem.description || '',
            label: matchingItem.description,
            fileId: Date.now(),
            sizeText: matchingItem.size || '',
            size: this.convertToBytes(matchingItem.size),
            storageId: matchingItem.storageId,
            storageProviderId: matchingItem.storageProviderId,
            type: matchingItem.contentType,
            deleteDisabled: true,
            isNew: false
          };

          attachments.push(attachmentDetail);
        }
      });

      result.push({
        requestNumber: requestNumber,
        documentList: attachments,
      });
    });

    return result;
  }

  convertToBytes(sizeStr: string): number {
    const units: { [key: string]: number } = {
      B: 1,
      KB: 1024,
      MB: 1024 ** 2,
      GB: 1024 ** 3,
      TB: 1024 ** 4,
    };

    const match = sizeStr.trim().match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i);

    if (!match) {
      return 0;
    }

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    return Math.round(value * (units[unit] || 1));
  }

  //this method maps the attachments to record form
  getAttachments(record: any, index: string, requestDocuments: any) {
    record.orderInfoAttachmentFile = [];
    record.orderInfoAttachmentFile = requestDocuments[index]?.documentList;
  }

  setRequests(data: any[]) {
    this.requests = data;
  }

  getRequests() {
    return this.requests;
  }

  setRequestDocuments(data: any[]) {
    this.requestDocuments = data;
  }

  getRequestDocuments() {
    return this.requestDocuments;
  }

  setAllDocuments(data: any[]) {
    this.documents = data;
  }

  getAllDocuments() {
    return this.documents;
  }
}
